import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, Video } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";

export default function BookingPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [slots, setSlots] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [selected, setSelected] = useState(null);
  const [booking, setBooking] = useState(false);

  const load = () => {
    api.get(`/cases/${caseId}`).then((r) => setBundle(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/slots/open").then((r) => setSlots(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [caseId]);

  const grouped = useMemo(() => {
    const required = bundle?.product?.interview_minutes;
    const g = {};
    (slots || []).filter((s) => !required || s.duration === required).forEach((s) => {
      const day = s.start.split("T")[0];
      g[day] = g[day] || [];
      g[day].push(s);
    });
    return g;
  }, [slots, bundle]);

  const book = async () => {
    if (!selected) return;
    setBooking(true);
    try {
      await api.post(`/cases/${caseId}/book`, { slot_id: selected.id });
      toast.success("Interview booked.");
      load();
      setSelected(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBooking(false);
    }
  };

  if (!bundle) return <DashboardShell title="Book your interview"><div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" /></DashboardShell>;

  if (bundle.appointment) {
    const a = bundle.appointment;
    const dtG = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const startD = new Date(a.start);
    const endD = new Date(startD.getTime() + (a.duration || 75) * 60000);
    const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`${bundle.product?.name || "Evaluation"} — Clinical Interview`)}&dates=${dtG(startD)}/${dtG(endD)}&details=${encodeURIComponent(`Secure video link: ${a.meeting_link}`)}&location=${encodeURIComponent(a.meeting_link)}`;
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(`${bundle.product?.name || "Evaluation"} — Clinical Interview`)}&startdt=${startD.toISOString()}&enddt=${endD.toISOString()}&body=${encodeURIComponent(`Secure video link: ${a.meeting_link}`)}&location=${encodeURIComponent(a.meeting_link)}`;
    return (
      <DashboardShell title="Your interview is scheduled" subtitle="A secure video link is below. Your clinician will review your intake and questionnaires beforehand.">
        <div className="card-warm p-10 max-w-2xl" data-testid="appointment-confirmation">
          <CalendarClock className="h-10 w-10 text-[#1E3F20]" />
          <p className="font-heading text-3xl font-semibold mt-4" data-testid="appointment-datetime">{new Date(a.start).toLocaleString()}</p>
          <p className="text-sm text-[#5C5C57] mt-2">{a.duration} minutes · with {bundle.clinician?.name}{bundle.clinician?.credentials ? `, ${bundle.clinician.credentials}` : ""}</p>
          <a href={a.meeting_link} target="_blank" rel="noreferrer" data-testid="meeting-link">
            <Button className="mt-6 rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
              <Video className="h-4 w-4 mr-2" /> Join video interview
            </Button>
          </a>
          <div className="mt-6">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#5C5C57] mb-3">Add to your calendar</p>
            <div className="flex flex-wrap gap-2" data-testid="calendar-options">
              <a href={gcalUrl} target="_blank" rel="noreferrer" data-testid="cal-google">
                <Button variant="outline" size="sm" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20]">Google Calendar</Button></a>
              <a href={outlookUrl} target="_blank" rel="noreferrer" data-testid="cal-outlook">
                <Button variant="outline" size="sm" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20]">Outlook</Button></a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/appointment.ics`} data-testid="cal-ics">
                <Button variant="outline" size="sm" className="rounded-full border-[#1E3F20]/30 text-[#1E3F20]">Apple / iCal (.ics)</Button></a>
            </div>
          </div>
          <div className="mt-6">
            <Button variant="ghost" data-testid="back-to-dashboard-btn" onClick={() => navigate("/app")} className="rounded-full text-[#5C5C57]">Back to my portal</Button>
          </div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Book your clinician interview" subtitle="Choose an available time. All interviews are conducted by secure video.">
      {slots === null ? (
        <div className="h-8 w-8 rounded-full border-2 border-[#1E3F20] border-t-transparent animate-spin" />
      ) : Object.keys(grouped).length === 0 ? (
        <div className="card-warm p-12 text-center max-w-xl" data-testid="no-slots">
          <p className="font-heading text-2xl font-semibold">No {bundle.product?.interview_minutes}-minute appointments open right now</p>
          <p className="text-sm text-[#5C5C57] mt-2">Your evaluation requires a {bundle.product?.interview_minutes}-minute interview. Our clinicians add new availability regularly — please check back soon.</p>
        </div>
      ) : (
        <div className="space-y-8 max-w-4xl">
          <p className="text-sm text-[#5C5C57]" data-testid="duration-note">This evaluation requires a <span className="font-semibold text-[#1C1C19]">{bundle.product?.interview_minutes}-minute</span> clinician interview. Only matching appointments are shown.</p>
          {Object.entries(grouped).map(([day, daySlots]) => (
            <div key={day}>
              <p className="label-caps text-[#7E9B82] mb-3">{new Date(day + "T12:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {daySlots.map((s) => (
                  <button key={s.id} type="button" data-testid={`slot-${s.id}`} onClick={() => setSelected(s)}
                    className={`p-4 rounded-xl border text-left transition-colors ${selected?.id === s.id ? "border-[#1E3F20] bg-[#1E3F20]/5" : "border-[#E5E5DF] bg-white hover:border-[#7E9B82]"}`}>
                    <p className="font-semibold">{new Date(s.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</p>
                    <p className="text-xs text-[#5C5C57] mt-1">{s.clinician_name}{s.clinician_credentials ? `, ${s.clinician_credentials}` : ""}</p>
                    <p className="text-xs text-[#5C5C57]">{s.duration} min</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <Button data-testid="confirm-booking-btn" disabled={!selected || booking} onClick={book}
            className="rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white px-8 h-11 transition-colors">
            {booking ? "Booking…" : "Confirm appointment"}
          </Button>
        </div>
      )}
    </DashboardShell>
  );
}
